import React, { useState, useEffect } from 'react';
import { processFileData } from '../utils/fileAnalysisProcessing';
import Editor from "@monaco-editor/react";
import {
  Box,
  ColumnLayout,
  Container,
  Header,
  SpaceBetween,
  Table,
  TextFilter,
  Toggle,
  Select,
  StatusIndicator,
  Link,
  Tabs,
  Modal,
  Button,
  ProgressBar,
  Cards,
  TagEditor,
  FormField,
  Pagination,
  ButtonDropdown,
  ExpandableSection,
} from '@cloudscape-design/components';

export function CodeAnalysisTab({ project }) {
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [selectedView, setSelectedView] = useState({ value: 'table' });
  const [showRawJson, setShowRawJson] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    type: { value: 'all' },
    complexity: { value: 'all' }
  });
  const [showDetails, setShowDetails] = useState(false);
  

  // Load and process files
  useEffect(() => {
    const fileNames = [
      'SpringBootMonolithApplication.json',
      'Customer.json',
      'CustomerEntity.json',
      'CustomerRepo.json',
      'CustomerService.json',
      'Item.json',
      'ItemEntity.json',
      'ItemRepo.json',
      'ItemService.json',
      'Order.json',
      'OrderEntity.json',
      'OrderRepo.json',
      'OrderService.json',
      'RepositoryCustomerService.json',
      'RepositoryItemService.json',
      'RepositoryOrderService.json',
      'SpringBootMonolithApplication.json',
    ];
  
    Promise.all(
      fileNames.map(fileName =>
        import(`../data/file_analyses/${fileName}`).then(module => processFileData(module.default))
      )
    ).then(processedFiles => {
      setFiles(processedFiles);
    });
  }, []);

  const filteredFiles = files.filter(file =>
    file.name.toLowerCase().includes(filterText.toLowerCase())
  );

  // Modified table columns to match actual data
  const tableColumns = [
    {
      id: 'name',
      header: 'File name',
      cell: item => (
        <Link onFollow={() => setSelectedFile(item)}>
          {item.name}
        </Link>
      ),
      sortingField: 'name'
    },
    {
      id: 'type',
      header: 'Type',
      cell: item => item.type,
      sortingField: 'type'
    },
    {
      id: 'metrics',
      header: 'Methods/Fields',
      cell: item => `${item.metrics.methods}/${item.metrics.fields}`
    },
    {
      id: 'confidence',
      header: 'Classification confidence',
      cell: item => (
        <ProgressBar
          value={item.classification.confidence}
          label={`${item.classification.confidence}%`}
        />
      ),
      sortingField: 'classification.confidence'
    },
    {
      id: 'relationships',
      header: 'Relationships',
      cell: item => item.metrics.relationships
    }
  ];

  // Updated modal content
  const renderFileDetails = (file) => (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h3"
            actions={
              <Button onClick={() => setShowRawJson(!showRawJson)}>
                {showRawJson ? 'Show Analysis' : 'Show Raw JSON'}
              </Button>
            }
          >
            Classification Details
          </Header>
        }
      >
        {showRawJson ? (
          <Editor
            height="60vh"
            defaultLanguage="json"
            value={JSON.stringify(file.rawData, null, 2)}
            options={{
              readOnly: true,
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              fontSize: 14
            }}
          />
        ) : (
          <Tabs
          tabs={[
            {
              label: "Overview",
              id: "overview",
              content: (
                <SpaceBetween size="l">
                  <ColumnLayout columns={2}>
                    <SpaceBetween size="s">
                      <Box variant="awsui-key-label">Type</Box>
                      <Box>{file.classification?.type}</Box>
                      <Box variant="awsui-key-label">Package</Box>
                      <Box>{file.path}</Box>
                      <Box variant="awsui-key-label">Confidence Score</Box>
                      <Box>{file.classification?.confidence}%</Box>
                    </SpaceBetween>
                    <SpaceBetween size="s">
                      <Box variant="awsui-key-label">Classification Evidence</Box>
                      <ul>
                        {file.classification?.evidence?.map((evidence, index) => (
                          <li key={index}>{evidence}</li>
                        ))}
                      </ul>
                    </SpaceBetween>
                  </ColumnLayout>
                  
                  {file.classification?.details?.operations?.length > 0 && (
                    <Container header={<Header variant="h4">Operations</Header>}>
                      <Table
                        columnDefinitions={[
                          {
                            header: "Operation",
                            cell: item => item.name
                          },
                          {
                            header: "Business Process",
                            cell: item => item.business_process
                          },
                          {
                            header: "Transaction Scope",
                            cell: item => item.transaction_scope
                          },
                          {
                            header: "Affected Entities",
                            cell: item => (item.entities_affected || []).join(", ")
                          }
                        ]}
                        items={file.classification.details.operations}
                        variant="embedded"
                      />
                    </Container>
                  )}
  
                  {file.classification?.details?.dependencies?.length > 0 && (
                    <Container header={<Header variant="h4">Dependencies</Header>}>
                      <TagEditor
                        tags={file.classification.details.dependencies.map(dep => ({
                          key: dep,
                          value: dep
                        }))}
                        readOnly
                      />
                    </Container>
                  )}
                </SpaceBetween>
              )
            },
            {
              label: "Structure",
              id: "structure",
              content: (
                <SpaceBetween size="l">
                  <Container header={<Header variant="h4">Class Information</Header>}>
                    <ColumnLayout columns={2}>
                      <SpaceBetween size="s">
                        <Box variant="awsui-key-label">Annotations</Box>
                        <Box>{file.structure?.annotations?.join(', ') || 'None'}</Box>
                      </SpaceBetween>
                      <SpaceBetween size="s">
                        <Box variant="awsui-key-label">Imports</Box>
                        <Box>{file.parsed_class?.imports?.join(',\n') || 'None'}</Box>
                      </SpaceBetween>
                    </ColumnLayout>
                  </Container>
  
                  <Container header={<Header variant="h4">Methods</Header>}>
                    <Table
                      columnDefinitions={[
                        {
                          header: "Name",
                          cell: item => item.name
                        },
                        {
                          header: "Return Type",
                          cell: item => item.properties?.return_type
                        },
                        {
                          header: "Visibility",
                          cell: item => item.properties?.visibility
                        },
                        {
                          header: "Static",
                          cell: item => item.properties?.is_static ? "Yes" : "No"
                        },
                        {
                          header: "Annotations",
                          cell: item => (item.properties?.annotations || []).join(", ")
                        },
                        {
                          header: "Parameters",
                          cell: item => (item.properties?.parameters || [])
                            .map(param => `${param.type} ${param.name}`)
                            .join(", ")
                        }
                      ]}
                      items={file.structure?.methods || []}
                      variant="embedded"
                    />
                  </Container>
  
                  {(file.structure?.fields?.length > 0) && (
                    <Container header={<Header variant="h4">Fields</Header>}>
                      <Table
                        columnDefinitions={[
                          {
                            header: "Name",
                            cell: item => item.name
                          },
                          {
                            header: "Type",
                            cell: item => item.properties?.type
                          },
                          {
                            header: "Visibility",
                            cell: item => item.properties?.visibility
                          },
                          {
                            header: "Static",
                            cell: item => item.properties?.is_static ? "Yes" : "No"
                          },
                          {
                            header: "Final",
                            cell: item => item.properties?.is_final ? "Yes" : "No"
                          },
                          {
                            header: "Annotations",
                            cell: item => (item.properties?.annotations || []).join(", ")
                          }
                        ]}
                        items={file.structure.fields}
                        variant="embedded"
                      />
                    </Container>
                  )}
                </SpaceBetween>
              )
            },
            {
              label: "Relationships",
              id: "relationships",
              content: (
                <SpaceBetween size="l">
                  {Object.entries(file.relationships || {}).map(([type, relations]) => (
                    <Container header={<Header variant="h4">{type}</Header>} key={type}>
                      <Table
                        columnDefinitions={[
                          {
                            header: "From",
                            cell: item => (
                              <SpaceBetween size="xs">
                                <Box>{item.from?.type}</Box>
                                <Box variant="small">{item.from?.name}</Box>
                              </SpaceBetween>
                            )
                          },
                          {
                            header: "To",
                            cell: item => (
                              <SpaceBetween size="xs">
                                <Box>{item.to?.type}</Box>
                                <Box variant="small">{item.to?.name}</Box>
                              </SpaceBetween>
                            )
                          }
                        ]}
                        items={relations || []}
                        variant="embedded"
                      />
                    </Container>
                  ))}
                </SpaceBetween>
              )
            },
            {
              label: "Data & Operations",
              id: "data-operations",
              content: (
                <SpaceBetween size="l">
                  {(file.data_relationships?.entity_relationships?.length > 0) && (
                    <Container header={<Header variant="h4">Entity Relationships</Header>}>
                      <Table
                        columnDefinitions={[
                          {
                            header: "Related Entity",
                            cell: item => item.related_entity
                          },
                          {
                            header: "Type",
                            cell: item => item.type
                          },
                          {
                            header: "Cardinality",
                            cell: item => item.cardinality
                          },
                          {
                            header: "Lifecycle",
                            cell: item => item.lifecycle_dependency
                          }
                        ]}
                        items={file.data_relationships.entity_relationships}
                        variant="embedded"
                      />
                    </Container>
                  )}
  
                  {(file.operation_flows?.business_workflows?.length > 0) && (
                    <Container header={<Header variant="h4">Business Workflows</Header>}>
                      {file.operation_flows.business_workflows.map((workflow, index) => (
                        <ExpandableSection header={workflow.name} key={index}>
                          <Table
                            columnDefinitions={[
                              {
                                header: "Operation",
                                cell: item => item.operation
                              },
                              {
                                header: "Service",
                                cell: item => item.service
                              },
                              {
                                header: "Entities Involved",
                                cell: item => (item.entities_involved || []).join(", ")
                              }
                            ]}
                            items={workflow.steps || []}
                            variant="embedded"
                          />
                        </ExpandableSection>
                      ))}
                    </Container>
                  )}
                </SpaceBetween>
              )
            }
          ]}
        />
        )}
      </Container>
    </SpaceBetween>
  );
  

  return (
    <SpaceBetween size="l">
      {/* Analysis Controls */}
      <Container
        header={
          <Header
            variant="h2"
            description="Detailed analysis of source code files"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <ButtonDropdown
                  items={[
                    { text: 'Export results', id: 'export' },
                    { text: 'Refresh analysis', id: 'refresh' },
                    { text: 'Configure analysis', id: 'configure' }
                  ]}
                >
                  Actions
                </ButtonDropdown>
                <Select
                  selectedOption={selectedView}
                  onChange={({ detail }) => setSelectedView(detail.selectedOption)}
                  options={[
                    { label: 'Table view', value: 'table' },
                    { label: 'Card view', value: 'card' }
                  ]}
                />
              </SpaceBetween>
            }
          >
            Code Analysis Results
          </Header>
        }
      >
        <SpaceBetween size="m">
          {/* Filters */}
          <ColumnLayout columns={4}>
            <TextFilter
              filteringText={filterText}
              onChange={({ detail }) => setFilterText(detail.filteringText)}
              placeholder="Find files"
            />
            <FormField label="File type">
              <Select
                selectedOption={selectedFilters.type}
                onChange={({ detail }) => 
                  setSelectedFilters(prev => ({ ...prev, type: detail.selectedOption }))
                }
                options={[
                  { label: 'All types', value: 'all' },
                  { label: 'Service', value: 'service' },
                  { label: 'Repository', value: 'repository' },
                  { label: 'Entity', value: 'entity' },
                  { label: 'Controller', value: 'controller' }
                ]}
              />
            </FormField>
            <FormField label="Complexity">
              <Select
                selectedOption={selectedFilters.complexity}
                onChange={({ detail }) =>
                  setSelectedFilters(prev => ({ ...prev, complexity: detail.selectedOption }))
                }
                options={[
                  { label: 'All', value: 'all' },
                  { label: 'High', value: 'high' },
                  { label: 'Medium', value: 'medium' },
                  { label: 'Low', value: 'low' }
                ]}
              />
            </FormField>
            <Toggle
              onChange={({ detail }) => setShowDetails(detail.checked)}
              checked={showDetails}
            >
              Show details
            </Toggle>
          </ColumnLayout>

          {/* Results View */}
          {selectedView.value === 'table' ? (
            <Table
              columnDefinitions={tableColumns}
              items={filteredFiles}
              loading={false}
              loadingText="Loading code analysis"
              selectionType="single"
              onSelectionChange={({ detail }) => 
                setSelectedFile(detail.selectedItems[0])
              }
              pagination={<Pagination />}
              variant="container"
              stickyHeader
              stripedRows
            />
          ) : (
            <Cards
              cardDefinition={{
                header: item => (
                  <Link onFollow={() => setSelectedFile(item)}>
                    {item.name}
                  </Link>
                ),
                sections: [
                  {
                    id: 'type',
                    header: 'Classification',
                    content: item => (
                      <SpaceBetween size="xs">
                        <Box>{item.type}</Box>
                        <Box variant="small">
                          Confidence: {item.classification.confidence}%
                        </Box>
                      </SpaceBetween>
                    )
                  },
                  {
                    id: 'metrics',
                    header: 'Metrics',
                    content: item => (
                      <ColumnLayout columns={2}>
                        <Box>Methods: {item.metrics.methods}</Box>
                        <Box>Fields: {item.metrics.fields}</Box>
                        <Box>Imports: {item.metrics.imports}</Box>
                        <Box>Relationships: {item.metrics.relationships}</Box>
                      </ColumnLayout>
                    )
                  }
                ]
              }}
              items={filteredFiles}
              loading={false}
              loadingText="Loading code analysis"
              pagination={<Pagination />}
              cardLayout="grid"
            />
          )}
        </SpaceBetween>
      </Container>

      {/* File Details Modal */}
      <Modal
        visible={!!selectedFile}
        onDismiss={() => setSelectedFile(null)}
        size="large"
        header={selectedFile?.name}
      >
        {selectedFile && renderFileDetails(selectedFile)}
      </Modal>
    </SpaceBetween>
  );
};
